import React, { useState } from "react";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton,
  FieldInput,
  FormRow,
  Modal,
  ModalShell,
} from "@/components/ds";
import { Plus, X, Edit, AlertTriangle } from "@/components/ds/icons";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import IntegrationHealthBadge from "@/components/IntegrationHealthBadge";

interface BigCommerceConfig {
  id: number;
  siteId: number;
  displayName: string;
  storeHash: string;
  storeName: string;
  storeUrl: string;
  enabled: boolean;
  isActive?: boolean;
  disabledAt?: string | null;
  disabledBy?: string | null;
  createdAt: string;
  accessTokenMasked: string;
}

interface Props {
  configs: BigCommerceConfig[];
  onDelete(id: number): void;
  onRefresh(): void;
}

export default function BigCommerceAccounts({ configs, onDelete, onRefresh }: Props) {
  const navigate = useNavigate();
  const api = useApi();
  const [deleteTarget, setDeleteTarget] = useState<BigCommerceConfig | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [tokenEditTarget, setTokenEditTarget] = useState<BigCommerceConfig | null>(null);
  const [newToken, setNewToken] = useState("");
  const [tokenSaving, setTokenSaving] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  async function confirmDelete() {
    if (!deleteTarget) return;
    await api.admin.delete(`BigCommerce/Configs/${deleteTarget.id}`);
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  }

  async function saveDisplayName(config: BigCommerceConfig) {
    setSaving(true);
    try {
      await api.admin.put(`BigCommerce/Configs/${config.id}`, { displayName: editName });
      onRefresh();
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  }

  async function saveToken() {
    if (!tokenEditTarget || !newToken.trim()) return;
    setTokenSaving(true);
    setTokenError(null);
    try {
      const resp = await api.admin.put(`BigCommerce/Configs/${tokenEditTarget.id}`, { accessToken: newToken.trim() });

      if (!resp.ok) {
        // A non-2xx response can still have a JSON body — don't fall through to
        // "success" just because res.json() doesn't throw. Surface status + body
        // so this isn't a dead end when the backend is actually erroring (a real
        // 500 here previously showed as an identical, useless generic message).
        let bodyText = "";
        try { bodyText = await resp.text(); } catch { /* body unreadable — status alone is still useful */ }
        console.error(`BigCommerce token update failed: HTTP ${resp.status} ${resp.statusText}`, bodyText);
        setTokenError(`Update failed (HTTP ${resp.status}): ${bodyText || resp.statusText || "no response body"}`);
        return;
      }

      const data = await resp.json();
      if (data?.connectionWarning) {
        setTokenError(`Token saved but connection test failed: ${data.connectionWarning}`);
      } else {
        setTokenEditTarget(null);
        setNewToken("");
        onRefresh();
      }
    } catch (err) {
      // Network/CORS-level failure — the request never got a response at all.
      console.error("BigCommerce token update threw before a response was received:", err);
      setTokenError(
        err instanceof TypeError
          ? "Could not reach the server (network error or CORS block). Check your connection and try again — if this persists, the backend may be down."
          : `Failed to update token: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setTokenSaving(false);
    }
  }

  async function toggleEnabled(config: BigCommerceConfig) {
    await api.admin.put(`BigCommerce/Configs/${config.id}`, { enabled: !config.enabled });
    onRefresh();
  }

  async function toggleActive(config: BigCommerceConfig) {
    await api.admin.post(`BigCommerce/Configs/${config.id}/toggle-active`, {});
    onRefresh();
  }

  return (
    <SectionCard
      title="Accounts"
      description="BigCommerce stores connected to this site."
      className="mb-4"
      action={
        <PrimaryButton onClick={() => navigate("/integrations/bigcommerce/create")}>
          <Plus className="w-4 h-4" />
          Add account
        </PrimaryButton>
      }
    >
      {configs.length === 0 ? (
        <div className="py-8 text-center">
          <div className="text-zinc-500 text-sm">No BigCommerce stores connected yet.</div>
          <div className="text-zinc-400 text-xs mt-1">Click "Add account" above to connect your store.</div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-zinc-500 text-xs border-b border-zinc-200">
              <th className="pb-2 font-medium">Store</th>
              <th className="pb-2 font-medium w-36">Store hash</th>
              <th className="pb-2 font-medium w-36">Status</th>
              <th className="pb-2 w-px whitespace-nowrap"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {configs.map((config) => (
              <tr key={config.id}>
                <td className="py-3 pr-4">
                  {editingId === config.id ? (
                    <div className="flex gap-2 items-center">
                      <FieldInput
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveDisplayName(config); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <PrimaryButton onClick={() => saveDisplayName(config)} disabled={saving}>Save</PrimaryButton>
                      <GhostButton onClick={() => setEditingId(null)}>Cancel</GhostButton>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800">{config.displayName || config.storeName || "BigCommerce"}</span>
                      <IconButton
                        aria-label="Rename store"
                        onClick={() => { setEditingId(config.id); setEditName(config.displayName); }}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </IconButton>
                    </div>
                  )}
                  <div className="text-xs text-zinc-400 mt-0.5">{config.accessTokenMasked}</div>
                </td>
                <td className="py-3 pr-4 text-zinc-500 text-xs font-mono align-middle">{config.storeHash || "—"}</td>
                <td className="py-3 pr-4 align-middle">
                  <div className="flex flex-col gap-1 items-start">
                    <button
                      type="button"
                      onClick={() => toggleEnabled(config)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${config.enabled ? "bg-green-50 border-green-200 text-green-700" : "bg-zinc-100 border-zinc-200 text-zinc-500"}`}
                    >
                      {config.enabled ? "Enabled" : "Disabled"}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleActive(config)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${(config.isActive ?? true) ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}
                      title={(config.isActive ?? true) ? "Click to pause integration" : "Click to resume integration"}
                    >
                      {(config.isActive ?? true) ? "Active" : "Paused"}
                    </button>
                    {!(config.isActive ?? true) && config.disabledAt && (
                      <div className="text-xs text-amber-600 mt-0.5">
                        Paused {new Date(config.disabledAt).toLocaleDateString()}{config.disabledBy ? ` by ${config.disabledBy}` : ""}
                      </div>
                    )}
                    <IntegrationHealthBadge slug="BigCommerce" className="mt-1" />
                  </div>
                </td>
                <td className="py-3 pl-4 align-middle whitespace-nowrap">
                  <div className="flex items-center gap-6">
                    <GhostButton onClick={() => { setTokenEditTarget(config); setNewToken(""); setTokenError(null); }}>
                      Update token
                    </GhostButton>
                    <GhostButton className="!text-zinc-400 hover:!text-red-600" onClick={() => setDeleteTarget(config)}>
                      Remove
                    </GhostButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal open={!!tokenEditTarget} size="lg" onClose={() => setTokenEditTarget(null)} closeOnEsc={false} closeOnBackdropClick={false}>
            <ModalShell.Header title="Update access token" onClose={() => setTokenEditTarget(null)} />
            <ModalShell.Body>
              <p className="text-sm text-zinc-600 mb-1">
                Store: <strong>{tokenEditTarget?.displayName || tokenEditTarget?.storeName}</strong>
              </p>
              <p className="text-xs text-zinc-400 mb-4">
                Current token: <span className="font-mono">{tokenEditTarget?.accessTokenMasked}</span>
              </p>
              <FormRow label="New access token" hint="Generate a new token in BigCommerce → Settings → API → Store-level API accounts.">
                <FieldInput
                  type="password"
                  className="font-mono"
                  placeholder="Paste your new BigCommerce access token"
                  value={newToken}
                  onChange={(e) => setNewToken(e.target.value)}
                  autoFocus
                />
              </FormRow>
              {tokenError && <p className="text-sm text-red-600 mt-3">{tokenError}</p>}
            </ModalShell.Body>
            <ModalShell.Footer>
              <SecondaryButton onClick={() => setTokenEditTarget(null)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={saveToken} disabled={tokenSaving || !newToken.trim()}>
                {tokenSaving ? "Saving…" : "Save & test"}
              </PrimaryButton>
            </ModalShell.Footer>
      </Modal>

      <Modal open={!!deleteTarget} size="md" onClose={() => setDeleteTarget(null)} closeOnEsc={false} closeOnBackdropClick={false}>
            <ModalShell.Header
              title="Remove account"
              onClose={() => setDeleteTarget(null)}
            />
            <ModalShell.Body>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-zinc-600">
                  Remove <strong>{deleteTarget?.displayName || "this account"}</strong>? This cannot be undone.
                </p>
              </div>
            </ModalShell.Body>
            <ModalShell.Footer>
              <SecondaryButton onClick={() => setDeleteTarget(null)}>Cancel</SecondaryButton>
              <PrimaryButton onClick={confirmDelete}>Remove</PrimaryButton>
            </ModalShell.Footer>
      </Modal>
    </SectionCard>
  );
}
